import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { auth, users } from '../services/supabase';
import { UserProfile } from '../types/user';

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { user }, error } = await auth.getCurrentUser();
        if (error) throw error;

        if (user) {
          // Fetch user profile
          const { data: profile } = await users.getUserProfile(user.id);
          
          setAuthState(prev => ({
            ...prev,
            user,
            profile: profile || null,
            loading: false,
          }));
        } else {
          setAuthState(prev => ({
            ...prev,
            loading: false,
          }));
        }
      } catch (error) {
        setAuthState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Authentication error',
          loading: false,
        }));
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = auth.onAuthStateChange(async (event, session) => {
      setAuthState(prev => ({
        ...prev,
        session,
        user: session?.user || null,
      }));

      if (session?.user) {
        try {
          const { data: profile } = await users.getUserProfile(session.user.id);
          
          setAuthState(prev => ({
            ...prev,
            profile: profile || null,
            loading: false,
          }));
        } catch (error) {
          setAuthState(prev => ({
            ...prev,
            error: error instanceof Error ? error.message : 'Profile fetch error',
            loading: false,
          }));
        }
      } else {
        setAuthState(prev => ({
          ...prev,
          profile: null,
          loading: false,
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata?: Record<string, any>) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { data, error } = await auth.signUp(email, password, metadata);
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign up failed';
      setAuthState(prev => ({ ...prev, error: errorMessage, loading: false }));
      return { data: null, error: errorMessage };
    }
  };

  const signIn = async (email: string, password: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { data, error } = await auth.signIn(email, password);
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
      setAuthState(prev => ({ ...prev, error: errorMessage, loading: false }));
      return { data: null, error: errorMessage };
    }
  };

  const signInWithGoogle = async (redirectTo?: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { data, error } = await auth.signInWithGoogle(redirectTo);
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Google sign in failed';
      setAuthState(prev => ({ ...prev, error: errorMessage, loading: false }));
      return { data: null, error: errorMessage };
    }
  };

  const signOut = async () => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { error } = await auth.signOut();
      if (error) throw error;
      
      setAuthState({
        user: null,
        session: null,
        profile: null,
        loading: false,
        error: null,
      });
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Sign out failed',
        loading: false,
      }));
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!authState.user) return { error: 'No authenticated user' };

    try {
      const { error } = await users.updateUserProfile(authState.user.id, updates);
      if (error) throw error;

      // Refresh profile
      const { data: profile } = await users.getUserProfile(authState.user.id);
      setAuthState(prev => ({
        ...prev,
        profile: profile || prev.profile,
      }));

      return { error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      return { error: errorMessage };
    }
  };

  return {
    ...authState,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    updateProfile,
    isAuthenticated: !!authState.user,
  };
};
